import { Alert, Button, Center, Group, Paper, PasswordInput, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconAlertTriangle, IconLeaf } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { api, errorText } from '../api';

const MIN = 10;

/**
 * Wymagana zmiana hasła (ustawiona przez administratora): ekran jak logowanie — obecne hasło, nowe, powtórzone, OK.
 * Do zmiany serwer przepuszcza tylko zmianę hasła, profil i wylogowanie; po zmianie wymóg znika i otwiera się CRM.
 */
export function ForcedPasswordChangePage() {
  const qc = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!current) return setError('Wpisz obecne hasło');
    if (next.length < MIN) return setError(`Nowe hasło musi mieć co najmniej ${MIN} znaków`);
    if (next !== repeat) return setError('Nowe hasła nie są takie same');
    if (next === current) return setError('Nowe hasło musi się różnić od obecnego');
    setBusy(true);
    setError(null);
    try {
      await api('/auth/password', { method: 'POST', body: { currentPassword: current, newPassword: next } });
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Center mih="100vh" px="md" style={{ background: 'var(--app-bg)' }}>
      <Stack w="100%" maw={400} gap="lg">
        <Group gap="sm">
          <ThemeIcon size={40} radius="md" variant="gradient" gradient={{ from: 'eko.6', to: 'teal.8', deg: 135 }}>
            <IconLeaf size={22} />
          </ThemeIcon>
          <Text fw={800} size="lg">
            Eko CRM
          </Text>
        </Group>
        <div>
          <Title order={2}>Musisz zmienić hasło</Title>
          <Text c="dimmed" size="sm">
            Nowe hasło: co najmniej {MIN} znaków.
          </Text>
        </div>
        <Paper withBorder shadow="sm" p="xl">
          <form onSubmit={submit} noValidate>
            <Stack>
              {error && (
                <Alert color="red" icon={<IconAlertTriangle />}>
                  {error}
                </Alert>
              )}
              <PasswordInput label="Obecne hasło" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.currentTarget.value)} required autoFocus size="md" />
              <PasswordInput label="Nowe hasło" autoComplete="new-password" value={next} onChange={(e) => setNext(e.currentTarget.value)} required size="md" />
              <PasswordInput label="Powtórz nowe hasło" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.currentTarget.value)} required size="md" />
              <Button type="submit" size="md" fullWidth loading={busy}>
                OK
              </Button>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </Center>
  );
}
